import { makeStyles, Theme } from "@material-ui/core";

const useStyles = makeStyles((theme: Theme) => ({
  background: {
    backgroundColor: "#F8FAFC",
    minHeight: "100vh",
    paddingBottom: 60,
  },
  container: {
    maxWidth: 1280,
    margin: "0 auto",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  },
  headerBanner: {
    background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 100%)",
    borderRadius: 24,
    padding: "36px 40px",
    color: "#FFFFFF",
    marginTop: 24,
    marginBottom: 32,
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.15)",
    [theme.breakpoints.down("sm")]: {
      padding: "24px 20px",
      borderRadius: 18,
    },
  },
  headerGlow: {
    position: "absolute",
    top: "-30%",
    right: "-10%",
    width: 300,
    height: 300,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(79, 70, 229, 0.35) 0%, rgba(0,0,0,0) 70%)",
    pointerEvents: "none",
  },
  headerBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(8px)",
    padding: "6px 14px",
    borderRadius: 100,
    fontSize: "0.8rem",
    fontWeight: 700,
    color: "#818CF8",
    letterSpacing: "0.05em",
    marginBottom: 12,
    border: "1px solid rgba(255, 255, 255, 0.15)",
  },
  headerTitle: {
    fontWeight: 900,
    fontSize: "1.75rem",
    letterSpacing: "-0.02em",
    marginBottom: 8,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.35rem",
    },
  },
  headerSubtitle: {
    color: "#94A3B8",
    fontSize: "0.95rem",
    maxWidth: 640,
    lineHeight: 1.6,
  },

  // Main Cards
  profileCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    border: "1px solid #E2E8F0",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
    padding: 36,
    marginBottom: 32,
    [theme.breakpoints.down("sm")]: {
      padding: 20,
      borderRadius: 18,
    },
  },
  avatarWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    paddingBottom: 24,
    marginBottom: 24,
    borderBottom: "1px solid #F1F5F9",
  },
  avatarBox: {
    position: "relative",
    width: 120,
    height: 120,
    marginBottom: 16,
    "& img": {
      width: 120,
      height: 120,
      borderRadius: "50%",
      objectFit: "cover",
      border: "4px solid #EEF2FF",
      boxShadow: "0 8px 20px rgba(79, 70, 229, 0.15)",
      transition: "all 0.3s ease",
    },
  },
  uploadAvatarBtn: {
    position: "absolute",
    bottom: 2,
    right: 2,
    width: 38,
    height: 38,
    borderRadius: "50%",
    backgroundColor: "#4F46E5",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(79, 70, 229, 0.4)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#4338CA",
      transform: "scale(1.1)",
    },
  },
  userName: {
    fontWeight: 800,
    fontSize: "1.35rem",
    color: "#0F172A",
    marginBottom: 4,
  },
  userEmail: {
    color: "#64748B",
    fontSize: "0.9rem",
    marginBottom: 12,
  },
  userRoleBadge: {
    backgroundColor: "#EEF2FF",
    color: "#4F46E5",
    fontWeight: 800,
    fontSize: "0.75rem",
    padding: "4px 14px",
    borderRadius: 100,
    letterSpacing: "0.04em",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },

  // Section Headers & Labels
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 24,
    paddingBottom: 12,
    borderBottom: "2px solid #F1F5F9",
  },
  sectionTitle: {
    fontWeight: 800,
    fontSize: "1.15rem",
    color: "#0F172A",
    letterSpacing: "0.01em",
  },
  formLabel: {
    fontWeight: 700,
    fontSize: "0.85rem",
    color: "#334155",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 6,
    letterSpacing: "0.02em",
  },
  fieldGroup: {
    marginBottom: 12,
  },

  // Unified Form Controls Container
  formContainer: {
    "& .MuiFormControl-root": {
      width: "100%",
      margin: 0,
      padding: "0 !important",
    },

    // 1. TextField / OutlinedInput styling (Height = 46px)
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      backgroundColor: "#F8FAFC !important",
      height: "46px !important",
      boxSizing: "border-box !important",
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: "#CBD5E1 !important",
        borderWidth: "1.5px !important",
        borderRadius: "12px !important",
      },
      "&:hover fieldset": {
        borderColor: "#94A3B8 !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#4F46E5 !important",
        borderWidth: "2px !important",
      },
      "&.Mui-disabled": {
        backgroundColor: "#F1F5F9 !important",
        "& fieldset": {
          borderColor: "#E2E8F0 !important",
        },
      },
    },

    // 2. SelectField styling (Height = 46px)
    "& div.MuiInputBase-root:not(.MuiOutlinedInput-root)": {
      borderRadius: "12px !important",
      backgroundColor: "#F8FAFC !important",
      border: "1.5px solid #CBD5E1 !important",
      height: "46px !important",
      transition: "all 0.2s ease",
      boxSizing: "border-box !important",
      padding: "0 !important",
      margin: "0 !important",
      display: "flex !important",
      alignItems: "center !important",
      "&:hover": {
        borderColor: "#94A3B8 !important",
      },
      "&:focus-within": {
        borderColor: "#4F46E5 !important",
        backgroundColor: "#FFFFFF !important",
        boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.15)",
      },
      "&:before, &:after": {
        display: "none !important",
      },
    },

    // Inputs & Select internal text padding & alignment
    "& .MuiInputBase-input": {
      padding: "10px 16px !important",
      fontSize: "0.925rem",
      color: "#0F172A",
      fontWeight: 600,
      height: "100% !important",
      boxSizing: "border-box !important",
      display: "flex !important",
      alignItems: "center !important",
    },
    "& .MuiSelect-select.MuiSelect-select": {
      padding: "10px 32px 10px 16px !important",
      fontSize: "0.925rem",
      color: "#0F172A",
      fontWeight: 600,
      backgroundColor: "transparent !important",
      borderRadius: "12px !important",
      display: "flex !important",
      alignItems: "center !important",
      height: "100% !important",
      boxSizing: "border-box !important",
    },

    "& .MuiSelect-icon": {
      right: 12,
      color: "#64748B",
    },
    "& .MuiInputLabel-root": {
      display: "none !important",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 4,
      marginTop: 4,
      fontSize: "0.75rem",
      fontWeight: 600,
    },
  },

  // Action Buttons
  btnSubmit: {
    backgroundColor: "#4F46E5",
    color: "#FFFFFF",
    fontWeight: 800,
    fontSize: "0.95rem",
    borderRadius: 12,
    padding: "12px 32px",
    textTransform: "none",
    boxShadow: "0 6px 18px rgba(79, 70, 229, 0.25)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#4338CA",
      boxShadow: "0 8px 24px rgba(79, 70, 229, 0.35)",
    },
  },

  // Legacy compatibility props
  content: {},
  avatar: {},
  upload__avatar: {},
  upload__avatar__camera: {},
  content__info: {},
  content__info__title: {},
  form: {},
  form__input: {},
  btn_submit: {},
  staffId: {},
  forgotPassword: {},
  btn_add: {},
  btn_delete: {},
  loading: {},
}));

export default useStyles;
